import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const STATUS_OPTIONS = [
  {
    value: 'PENDING',
    label: '대기중',
  },
  {
    value: 'PROCESSING',
    label: '처리중',
  },
  {
    value: 'COMPLETED',
    label: '완료',
  },
]

const REASON_OPTIONS = [
  {
    value: '부적절한 콘텐츠',
    label: '부적절한 콘텐츠',
  },
  {
    value: '광고/홍보성 콘텐츠',
    label: '광고/홍보성 콘텐츠',
  },
  {
    value: '사기/피싱 콘텐츠',
    label: '사기/피싱 콘텐츠',
  },
  {
    value: '기타',
    label: '기타',
  },
]

export default function Report() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)
  const status = useQueryValue('status', undefined)
  const navigate = useNavigate()

  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    undefined,
  )
  const [selectedReason, setSelectedReason] = useState<string | undefined>(
    undefined,
  )

  const { data, isFetching } = useApi({
    id: 'reportList',
    fn: api().adminReport.getReportList,
    params: {
      page,
      pageSize: 20,
      q,
      status: selectedStatus || status,
      reason: selectedReason,
    },
  })

  const selectedStatusLabels = useMemo(() => {
    if (!selectedStatus) {
      return '전체'
    }
    return (
      STATUS_OPTIONS.find((option) => option.value === selectedStatus)?.label ||
      '전체'
    )
  }, [selectedStatus])

  const selectedReasonLabels = useMemo(() => {
    if (!selectedReason) {
      return '전체'
    }
    return (
      REASON_OPTIONS.find((option) => option.value === selectedReason)?.label ||
      '전체'
    )
  }, [selectedReason])

  const handleStatusSelect = (status: string | undefined) => {
    setSelectedStatus(status)
  }

  const handleReasonSelect = (reason: string | undefined) => {
    setSelectedReason(reason)
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">상태 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedStatusLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              <DropdownMenuCheckboxItem
                checked={selectedStatus === undefined}
                onCheckedChange={() => handleStatusSelect(undefined)}
              >
                전체
              </DropdownMenuCheckboxItem>
              {STATUS_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selectedStatus === option.value}
                  onCheckedChange={() => handleStatusSelect(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">신고사유 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[180px] justify-between"
              >
                {selectedReasonLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[180px]">
              <DropdownMenuCheckboxItem
                checked={selectedReason === undefined}
                onCheckedChange={() => handleReasonSelect(undefined)}
              >
                전체
              </DropdownMenuCheckboxItem>
              {REASON_OPTIONS.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selectedReason === option.value}
                  onCheckedChange={() => handleReasonSelect(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SimpleTable
        isLoading={isFetching}
        data={data?.rows}
        options={{
          disableNew: true,
          disableSort: true,
        }}
        onRowClick={(row) => {
          navigate(`/report/detail?id=${row.id}`)
        }}
        columns={[
          {
            label: 'NO',
            key: 'id',
            isPrimary: true,
          },
          {
            label: '신고계정',
            key: 'user.email',
          },
          {
            label: '신고사유',
            key: 'reason',
          },
          {
            key: 'targetData.type',
            label: '게시판',
            valueMap: {
              communityArticle: '커뮤니티',
              clinicReview: '병원후기',
              clinicNewEquipment: '신규장비',
            },
          },
          {
            key: 'status',
            label: '상태',
            valueMap: {
              PENDING: '대기중',
              PROCESSING: '처리중',
              COMPLETED: '처리완료',
            },
          },
        ]}
      />
      <OffsetPagination page={page} total={data?.count} pageSize={20} />
    </ContentView>
  )
}
